import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Loader,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  RefreshCcw,
  Download,
  Search,
  Lightbulb,
  Users,
  MessageSquare,
  CalendarClock,
} from 'lucide-react';
import { supabase, STORAGE_BUCKET } from '../../lib/supabase';
import { fetchWithTimeout } from '../../lib/fetchWithTimeout';
import {
  findA1Document,
  getDocumentUrl,
  NPRMDocument,
  isTimelineRowStale,
  formatDeadlineCalendarDate,
} from '../../utils/nprmHelpers';
import {
  findNPRMInFederalRegister,
  FederalRegisterDocument,
} from '../../utils/federalRegisterHelpers';
import { notifyCacheHit } from '../../lib/cacheAlertService';
import { getFilingDetails } from '../../lib/ecfsBrowserClient';
import {
  downloadNPRMQuestionsDocx,
  getNPRMQuestionTypeStyle,
  NPRM_QUESTION_TYPE_LABEL,
} from '../../utils/nprmQuestionsDocx';

interface NPRMPreviewSectionProps {
  docketNumber: string;
  nprmCount: number | null;
  proceedingName?: string;
  questionsOnly?: boolean;
}

interface NPRMFiling {
  id_submission: string;
  date_received?: string;
  date_submission?: string;
  documents?: NPRMDocument[];
}

interface NPRMQuestion {
  question_id: number;
  question_text: string;
  question_type: string;
  key_topics?: string[];
  seeks_comment_on?: string[];
  section_heading?: string;
  paragraph_number?: number | string;
  confidence?: number;
  full_context?: string;
  referenced_statements?: string[];
  // New fields produced by the LLM-hybrid backend extraction.
  short_answer?: string;
  context_summary?: string;
  page_number?: number | null;
  paragraph_range?: string;
}

interface NPRMQuestionsData {
  docket_number?: string;
  total_questions?: number;
  all_questions?: NPRMQuestion[];
  key_topics?: string[];
  seeks_comment_on?: string[];
}

interface NPRMSummaryProposal {
  title?: string;
  detail?: string;
}

interface NPRMSummaryCommentTopic {
  label: string;
  paragraphs?: number[];
  pages?: number[];
}

interface NPRMSummaryAnalysis {
  exec_summary?: string;
  whats_proposed?: NPRMSummaryProposal[];
  who_is_affected?: string[];
  background?: string;
  // Grounded topics (objects with citations) or legacy raw-text strings.
  comment_topics?: Array<NPRMSummaryCommentTopic | string>;
  comment_topics_grounded?: boolean;
}

interface NPRMSummaryResponse {
  status?: string;
  analysis?: NPRMSummaryAnalysis;
  cached?: boolean;
}

export const NPRMPreviewSection: React.FC<NPRMPreviewSectionProps> = ({
  docketNumber,
  nprmCount,
  proceedingName,
  questionsOnly = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nprmFiling, setNprmFiling] = useState<NPRMFiling | null>(null);
  const [a1Document, setA1Document] = useState<NPRMDocument | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentText, setDocumentText] = useState<string | null>(null);
  const [questions, setQuestions] = useState<NPRMQuestion[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [nprmFileInStorage, setNprmFileInStorage] = useState<string | null>(null);
  const [uploadingNprm, setUploadingNprm] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // "Reprocess" regenerates + overwrites the cached NPRM questions for the whole
  // docket — a maintenance action, not something a regular reader should trigger.
  // Gate it (and the diagnostic empty-state) to admins only.
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);
  const [reprocessSuccessAt, setReprocessSuccessAt] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuestionType, setSelectedQuestionType] = useState<string>('all');
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);

  // Plain-English NPRM proposal summary (the high-level brief that
  // complements the granular questions list below).
  const [nprmSummary, setNprmSummary] = useState<NPRMSummaryAnalysis | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Comment / reply deadlines from the shared nprm_timelines row (populated
  // by TimelineSection on this same page). Read-only here — the single most
  // actionable thing for a reg team is "by when do we comment?"
  const [commentDeadline, setCommentDeadline] = useState<string | null>(null);
  const [replyCommentDeadline, setReplyCommentDeadline] = useState<string | null>(null);
  
  // Federal Register data (used for document info display)
  const [federalRegisterDoc, setFederalRegisterDoc] = useState<FederalRegisterDocument | null>(null);

  // Auth gating for "Reprocess" (admins only — see isAdmin note above)
  const ADMIN_EMAILS = ['nitintangellamudi@gmail.com'];
  const emailIsAdmin = (email?: string | null) =>
    !!email && ADMIN_EMAILS.includes(email.toLowerCase());
  useEffect(() => {
    let isMounted = true;
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!isMounted) return;
        setIsAuthenticated(!!user);
        setIsAdmin(emailIsAdmin(user?.email));
      } catch {
        if (!isMounted) return;
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    };

    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setIsAuthenticated(!!session?.user);
      setIsAdmin(emailIsAdmin(session?.user?.email));
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const applyQuestionsResult = (result: NPRMQuestionsData) => {
    // Set questions. The LLM-hybrid backend emits records without an
    // explicit question_id, so backfill from the array index so React
    // keys and the existing "Q{id}" labeling keep working.
    if (result.all_questions && Array.isArray(result.all_questions)) {
      const withIds = result.all_questions.map((q, idx) => ({
        ...q,
        question_id: q.question_id ?? idx + 1,
      }));
      setQuestions(withIds);
    } else {
      setQuestions([]);
    }

    // Extract topics from questions for summary display. Now prefers the
    // LLM-hybrid `short_answer` (topical label) and falls back to the
    // legacy `key_topics` / `seeks_comment_on` arrays.
    const extractedTopics: string[] = [];
    if (result.key_topics && Array.isArray(result.key_topics)) {
      extractedTopics.push(...result.key_topics);
    }
    if (result.seeks_comment_on && Array.isArray(result.seeks_comment_on)) {
      extractedTopics.push(...result.seeks_comment_on);
    }
    if (result.all_questions && Array.isArray(result.all_questions)) {
      result.all_questions.forEach((question) => {
        if (question.short_answer && question.short_answer.trim()) {
          extractedTopics.push(question.short_answer.trim());
        }
        if (question.key_topics && Array.isArray(question.key_topics)) {
          extractedTopics.push(...question.key_topics);
        }
        if (question.seeks_comment_on && Array.isArray(question.seeks_comment_on)) {
          extractedTopics.push(...question.seeks_comment_on);
        }
      });
    }

    const uniqueTopics = Array.from(new Set(extractedTopics)).filter(
      (topic) => topic && topic.trim().length > 0
    );
    setTopics(uniqueTopics);
  };

  // Fetch NPRM filing details and find A1 document
  useEffect(() => {
    if (!docketNumber || nprmCount === null || nprmCount === 0) {
      setLoading(false);
      return;
    }

    const fetchNPRMData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch NPRM filings
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-nprm-filings`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            docketNumber: docketNumber,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch NPRM filings: ${response.status}`);
        }

        const data = await response.json();
        notifyCacheHit(data, `NPRM filings for ${docketNumber}`);
        const filings = data?.filing || [];

        if (filings.length === 0) {
          setError('No NPRM filings found');
          setLoading(false);
          return;
        }

        // Get the most recent NPRM filing
        const mostRecentFiling = filings.sort((a: any, b: any) => {
          const dateA = new Date(a.date_received || a.date_submission || 0);
          const dateB = new Date(b.date_received || b.date_submission || 0);
          return dateB.getTime() - dateA.getTime();
        })[0];

        const submissionId = mostRecentFiling.id_submission;

        // Prefer embedded documents from fetch-nprm-filings result.
        // Some submission IDs can intermittently fail on detail fetch upstream.
        let documents = mostRecentFiling.documents || [];

        // Fallback to direct ecfsapi fetch only when documents are missing.
        if (!Array.isArray(documents) || documents.length === 0) {
          const filingDetails = await getFilingDetails(submissionId);
          if (filingDetails) {
            notifyCacheHit(filingDetails, `NPRM filing ${submissionId}`);
            documents = filingDetails.documents || [];
          } else {
            console.warn(
              `Filing details fetch failed for ${submissionId}; continuing with embedded documents only.`
            );
          }
        }

        if (documents.length === 0) {
          setError('No documents found in NPRM filing');
          setLoading(false);
          return;
        }

        // Find A1 document
        const a1Doc = findA1Document(documents);
        if (!a1Doc) {
          setError('No A1 document found in NPRM filing');
          setLoading(false);
          return;
        }

        setNprmFiling({
          id_submission: submissionId,
          date_received: mostRecentFiling.date_received,
          date_submission: mostRecentFiling.date_submission,
          documents,
        });
        setA1Document(a1Doc);

        // Get document URL
        const url = await getDocumentUrl(docketNumber, a1Doc, submissionId);
        setDocumentUrl(url);

        // Populate timeline with FCC release date
        const fccReleaseDate = mostRecentFiling.date_received || mostRecentFiling.date_submission;
        if (fccReleaseDate) {
          try {
            const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
            await fetch(timelineApiUrl, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                docketNumber: docketNumber,
                submissionId: submissionId,
                fccReleaseDate: fccReleaseDate,
                nprmName: proceedingName || a1Document.filename,
              }),
            });
            console.log('Timeline populated with FCC release date');
          } catch (timelineErr) {
            console.warn('Failed to populate timeline with FCC release date:', timelineErr);
          }
        }

        // Check if NPRM file exists in storage, and upload if needed
        const checkAndUploadNprm = async () => {
          try {
            // Check possible storage paths for the NPRM file
            const filerName = mostRecentFiling.filers?.[0]?.name || 'FCC';
            const sanitizedFilerName = filerName.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, '_');
            const possiblePaths = [
              `ecfs/${docketNumber}/NPRM/${submissionId}___${sanitizedFilerName}___${a1Doc.filename}`,
              `ecfs/${docketNumber}/NPRM/${a1Doc.filename}`,
            ];

            let fileFound = false;
            for (const path of possiblePaths) {
              try {
                const { data, error } = await supabase.storage
                  .from(STORAGE_BUCKET)
                  .list(path.split('/').slice(0, -1).join('/'), {
                    limit: 1000,
                  });

                if (!error && data) {
                  const fileName = path.split('/').pop();
                  const found = data.find((file) => file.name === fileName);
                  if (found) {
                    setNprmFileInStorage(path);
                    fileFound = true;
                    break;
                  }
                }
              } catch (err) {
                // Continue to next path
              }
            }

            // If file not found, upload it
            if (!fileFound && url) {
              setUploadingNprm(true);
              try {
                const uploadApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-ecfs-files`;
                const uploadResponse = await fetch(uploadApiUrl, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    src: url,
                    docketNumber: docketNumber,
                    filerName: filerName,
                    fileName: a1Doc.filename,
                    submissionId: submissionId,
                    filingType: 'NPRM',
                    expressComment: null,
                  }),
                });

                if (uploadResponse.ok) {
                  const uploadResult = await uploadResponse.json();
                  if (uploadResult.path) {
                    setNprmFileInStorage(uploadResult.path);
                    console.log('NPRM file uploaded to storage:', uploadResult.path);
                  }
                } else {
                  console.warn('Failed to upload NPRM file to storage:', await uploadResponse.text());
                }
              } catch (uploadErr) {
                console.error('Error uploading NPRM file:', uploadErr);
              } finally {
                setUploadingNprm(false);
              }
            } else if (fileFound) {
              console.log('NPRM file already in storage');
            }
          } catch (err) {
            console.error('Error checking/uploading NPRM file:', err);
          }
        };

        await checkAndUploadNprm();

        // Note: PDF text extraction for deadline parsing would require
        // a PDF parsing library (like pdf.js) or backend service.
        // For now, deadline extraction will work if text is available.
      } catch (err) {
        console.error('Error fetching NPRM data:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load NPRM data'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchNPRMData();
  }, [docketNumber, nprmCount]);

  // Load cached NPRM questions from storage
  const loadCachedQuestions = async (docketNum: string): Promise<NPRMQuestionsData | null> => {
    try {
      const questionsPath = `summaries/${docketNum}/NPRM/nprm_questions.json`;
      
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(questionsPath);

      if (error) {
        console.log('No cached NPRM questions found');
        return null;
      }

      if (data) {
        const text = await data.text();
        const cached = JSON.parse(text);
        console.log('Loaded cached NPRM questions:', cached);
        return cached;
      }
    } catch (err) {
      console.log('Could not load cached NPRM questions:', err);
    }
    return null;
  };

  // Save NPRM questions to storage cache
  const saveQuestionsToCache = async (docketNum: string, questionsData: NPRMQuestionsData) => {
    try {
      const questionsPath = `summaries/${docketNum}/NPRM/nprm_questions.json`;
      const cacheData = {
        ...questionsData,
        cached_at: new Date().toISOString(),
      };
      
      const blob = new Blob([JSON.stringify(cacheData, null, 2)], { type: 'application/json' });
      const { error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(questionsPath, blob, {
          contentType: 'application/json',
          upsert: true, // Overwrite if exists
        });

      if (error) {
        console.warn('Failed to save questions to cache:', error);
      } else {
        console.log('NPRM questions cached successfully');
      }
    } catch (err) {
      console.warn('Error saving questions to cache:', err);
    }
  };

  // Fetch NPRM questions (only after file is in storage)
  useEffect(() => {
    if (!a1Document || !docketNumber || !nprmFileInStorage) {
      return;
    }

    const fetchQuestions = async () => {
      try {
        setLoadingQuestions(true);
        setQuestionsError(null);
        setReprocessError(null);

        // First, try to load from cache
        const cachedData = await loadCachedQuestions(docketNumber);
        if (cachedData && cachedData.all_questions && cachedData.all_questions.length > 0) {
          console.log('Using cached NPRM questions');
          applyQuestionsResult(cachedData);
          
          setLoadingQuestions(false);
          return;
        }

        // If not cached, fetch from API
        // Extract filename from storage path (last part after /)
        const storageFilename = nprmFileInStorage.split('/').pop() || a1Document.filename;

        const apiUrl = `https://fcc-summarize-app-r4ac3.ondigitalocean.app/nprm_questions`;
        const params = new URLSearchParams({
          docket_number: docketNumber,
          nprm_filename: storageFilename,
          method: 'llm',
        });

        console.log('Fetching NPRM questions from API:', {
          docket_number: docketNumber,
          nprm_filename: storageFilename,
          url: `${apiUrl}?${params.toString()}`,
        });

        const response = await fetchWithTimeout(`${apiUrl}?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('API response error:', response.status, errorText);
          throw new Error(`Failed to fetch questions: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('NPRM API response:', result);

        // The backend now returns immediately with status='processing' for
        // long NPRMs (writes the result to Supabase storage when done).
        // Poll the cache file until it appears, then apply.
        if (result?.status === 'processing') {
          console.log('NPRM extraction running in background; polling cache...');
          const POLL_INTERVAL_MS = 4000;
          const MAX_ATTEMPTS = 75; // ~5 minutes
          let attempt = 0;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            attempt += 1;
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            const polled = await loadCachedQuestions(docketNumber);
            if (polled && polled.all_questions && polled.all_questions.length > 0) {
              console.log(`NPRM cache landed after ${attempt} polls`);
              applyQuestionsResult(polled);
              break;
            }
            if (attempt >= MAX_ATTEMPTS) {
              throw new Error(
                `NPRM extraction did not complete within ${(MAX_ATTEMPTS * POLL_INTERVAL_MS) / 1000}s. Try again later.`,
              );
            }
          }
        } else {
          // Backwards compatibility: a backend that still returns the full
          // payload synchronously (e.g. on cache hit).
          applyQuestionsResult(result as NPRMQuestionsData);
          if (result.all_questions && result.all_questions.length > 0) {
            await saveQuestionsToCache(docketNumber, result as NPRMQuestionsData);
          }
        }
      } catch (err) {
        console.error('Error fetching NPRM questions:', err);
        setQuestionsError(
          err instanceof Error ? err.message : 'Failed to fetch NPRM questions'
        );
      } finally {
        setLoadingQuestions(false);
      }
    };

    fetchQuestions();
  }, [a1Document, docketNumber, nprmFileInStorage]);

  // Download the cached NPRM summary from storage (the backend writes it
  // there when the background generation finishes). Returns the analysis or
  // null if not present yet.
  const loadCachedSummary = async (
    docketNum: string
  ): Promise<NPRMSummaryAnalysis | null> => {
    try {
      const path = `summaries/${docketNum}/NPRM/nprm_summary.json`;
      const { data, error } = await supabase.storage.from(STORAGE_BUCKET).download(path);
      if (error || !data) return null;
      const parsed = JSON.parse(await data.text());
      return (parsed?.analysis as NPRMSummaryAnalysis) ?? null;
    } catch {
      return null;
    }
  };

  // Fetch the plain-English NPRM summary once the file is in storage.
  // Skipped in questionsOnly mode (the summary is the "preview" enrichment).
  useEffect(() => {
    if (!a1Document || !docketNumber || !nprmFileInStorage) return;

    let cancelled = false;
    const fetchSummary = async () => {
      try {
        setLoadingSummary(true);
        setSummaryError(null);

        const storageFilename = nprmFileInStorage.split('/').pop() || a1Document.filename;
        const apiUrl = `https://fcc-summarize-app-r4ac3.ondigitalocean.app/nprm_summary`;
        const params = new URLSearchParams({
          docket_number: docketNumber,
          nprm_filename: storageFilename,
        });

        const response = await fetchWithTimeout(`${apiUrl}?${params.toString()}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Failed to fetch NPRM summary: ${response.status} - ${errorText}`);
        }

        const result: NPRMSummaryResponse = await response.json();
        if (cancelled) return;

        if (result?.status === 'ready' && result.analysis) {
          setNprmSummary(result.analysis);
        } else if (result?.status === 'processing') {
          // The backend generates long NPRM summaries in the background to
          // stay under the request gateway timeout; poll the storage cache
          // until the result lands (mirrors the NPRM questions flow).
          const POLL_INTERVAL_MS = 4000;
          const MAX_ATTEMPTS = 75; // ~5 minutes
          let landed = false;
          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            if (cancelled) return;
            const polled = await loadCachedSummary(docketNumber);
            if (polled) {
              setNprmSummary(polled);
              landed = true;
              break;
            }
          }
          if (!landed) {
            throw new Error('NPRM summary is taking longer than expected. Try again shortly.');
          }
        } else {
          // no_nprm / error — leave the card hidden rather than showing noise.
          setNprmSummary(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching NPRM summary:', err);
        setSummaryError(err instanceof Error ? err.message : 'Failed to load NPRM summary');
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [a1Document, docketNumber, nprmFileInStorage]);

  // Read the comment/reply deadlines from the nprm_timelines row. The
  // TimelineSection on this page owns populating that row; we only read it,
  // so the deadline shows up here once it's available (and stays hidden if
  // the FCC hasn't set a Federal Register comment window yet).
  useEffect(() => {
    if (!docketNumber) return;

    let cancelled = false;
    const fetchDeadline = async () => {
      try {
        const { data, error } = await supabase
          .from('nprm_timelines')
          .select(
            'fcc_release_date, federal_register_publication_date, comment_deadline, reply_comment_deadline'
          )
          .eq('docket_number', docketNumber)
          .order('created_at', { ascending: false })
          .limit(1);

        if (cancelled || error || !data || data.length === 0) return;
        const row = data[0] as {
          fcc_release_date?: string | null;
          federal_register_publication_date?: string | null;
          comment_deadline?: string | null;
          reply_comment_deadline?: string | null;
        };
        // Don't surface deadlines from a stale timeline row (FR date before
        // FCC release date — indicates a prior/superseded match).
        if (isTimelineRowStale(row)) return;
        setCommentDeadline(row.comment_deadline ?? null);
        setReplyCommentDeadline(row.reply_comment_deadline ?? null);
      } catch {
        // Non-fatal — the deadline pill simply won't render.
      }
    };

    fetchDeadline();
    return () => {
      cancelled = true;
    };
  }, [docketNumber]);

  const handleReprocessQuestions = async () => {
    if (!isAuthenticated) return;
    if (!a1Document || !docketNumber || !nprmFileInStorage) return;
    if (isReprocessing || loadingQuestions || uploadingNprm) return;

    const confirmed = window.confirm(
      'Reprocess NPRM questions now? This will regenerate questions and overwrite the cached results for this docket.'
    );
    if (!confirmed) return;

    try {
      setIsReprocessing(true);
      setReprocessError(null);
      setReprocessSuccessAt(null);
      setQuestionsError(null);

      const storageFilename = nprmFileInStorage.split('/').pop() || a1Document.filename;
      const apiUrl = `https://fcc-summarize-app-r4ac3.ondigitalocean.app/nprm_questions`;
      const params = new URLSearchParams({
        docket_number: docketNumber,
        nprm_filename: storageFilename,
        method: 'llm',
      });

      const response = await fetchWithTimeout(`${apiUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const lowerText = errorText.toLowerCase();
        const hasDebugLogSignature =
          lowerText.includes('no such file or directory') &&
          lowerText.includes('debug.log');
        if (response.status >= 500 && hasDebugLogSignature) {
          throw new Error(
            'Reprocess temporarily unavailable on backend; using cached questions when available.'
          );
        }
        throw new Error(`Failed to reprocess questions: ${response.status} - ${errorText}`);
      }

      const result: NPRMQuestionsData = await response.json();
      applyQuestionsResult(result);

      // Overwrite cache on success (even if empty) so users can clear stale results.
      await saveQuestionsToCache(docketNumber, result);

      setReprocessSuccessAt(new Date().toISOString());
    } catch (err) {
      console.error('Error reprocessing NPRM questions:', err);
      // Graceful fallback: on upstream reprocess errors, keep UI usable by
      // restoring cached questions instead of leaving an error-only state.
      const cached = await loadCachedQuestions(docketNumber);
      if (cached) {
        applyQuestionsResult(cached);
        setReprocessError(
          err instanceof Error
            ? `${err.message} Showing cached questions.`
            : 'Reprocess failed; showing cached questions.'
        );
      } else {
        setReprocessError(err instanceof Error ? err.message : 'Failed to reprocess NPRM questions');
      }
    } finally {
      setIsReprocessing(false);
    }
  };

  // Fetch Federal Register data for document info display and timeline population
  const fetchFederalRegisterData = async () => {
    if (!docketNumber || !a1Document) {
      return;
    }

    try {
      // Use proceeding name if available, otherwise fall back to document filename
      const nprmTitles = proceedingName ? [proceedingName] : [a1Document.filename];

      const matchResult = await findNPRMInFederalRegister(docketNumber, nprmTitles);

      if (matchResult.document && matchResult.publicationDate) {
        setFederalRegisterDoc(matchResult.document);
        
        // Update timeline with Federal Register data
        // Let TimelineSection extract actual deadlines from Federal Register text
        if (nprmFiling?.id_submission) {
          try {
            const fccReleaseDate = nprmFiling.date_received || nprmFiling.date_submission;
            if (fccReleaseDate) {
              const timelineApiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/populate-nprm-timeline`;
              await fetch(timelineApiUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  docketNumber: docketNumber,
                  submissionId: nprmFiling.id_submission,
                  fccReleaseDate: fccReleaseDate,
                  federalRegisterPublicationDate: matchResult.publicationDate.toISOString(),
                  commentDeadline: null,
                  replyCommentDeadline: null,
                  nprmName: matchResult.document?.title || proceedingName || a1Document.filename,
                }),
              });
            }
          } catch (timelineErr) {
            console.warn('Failed to update timeline with Federal Register data:', timelineErr);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching Federal Register data:', err);
    }
  };

  useEffect(() => {
    if (!docketNumber || !a1Document) {
      return;
    }
    fetchFederalRegisterData();
  }, [docketNumber, a1Document, proceedingName]);

  // Unique question types present in the current data, for the filter chips.
  const questionTypes = useMemo(() => {
    const types = new Set<string>();
    questions.forEach((q) => {
      if (q.question_type && q.question_type.trim()) types.add(q.question_type);
    });
    return Array.from(types).sort();
  }, [questions]);

  // Apply text search + type filter to the questions list.
  const filteredQuestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return questions.filter((question) => {
      if (selectedQuestionType !== 'all' && question.question_type !== selectedQuestionType) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        question.question_text,
        question.short_answer,
        question.context_summary,
        question.question_type,
        question.paragraph_number != null ? `paragraph ${question.paragraph_number}` : '',
        question.page_number != null ? `page ${question.page_number}` : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [questions, searchQuery, selectedQuestionType]);

  const handleDownloadDocx = async () => {
    if (!questions.length) return;
    try {
      setIsDownloadingDocx(true);
      await downloadNPRMQuestionsDocx({
        docketNumber,
        totalQuestions: questions.length,
        nprmFilename: a1Document?.filename,
        proceedingName,
        questions: questions.map((q) => ({
          question_id: q.question_id,
          question_text: q.question_text,
          question_type: q.question_type,
          short_answer: q.short_answer,
          context_summary: q.context_summary,
          paragraph_number: q.paragraph_number,
          page_number: q.page_number ?? undefined,
        })),
      });
    } catch (err) {
      console.error('Failed to build NPRM questions docx:', err);
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  // Don't render if no NPRM
  if (nprmCount === null || nprmCount === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 text-green-600 animate-spin" />
          <span className="ml-3 text-gray-600">
            {questionsOnly ? 'Loading NPRM questions...' : 'Loading NPRM preview...'}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8 backdrop-blur-xl bg-white/90 border border-red-300/50 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-semibold mb-2">Error Loading NPRM</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!a1Document || !documentUrl) {
    return null;
  }

  return (
    <div className="mb-8 backdrop-blur-xl bg-white/90 border border-green-300/50 rounded-lg p-6 shadow-sm">
      <div className="flex items-center space-x-3 mb-6">
        <FileText className="w-6 h-6 text-green-600" />
        <h2 className="text-2xl font-bold text-black">
          {questionsOnly ? 'NPRM Questions' : 'NPRM Preview'}
        </h2>
      </div>

      {/* Document Info */}
      {!questionsOnly && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {federalRegisterDoc?.title ? (
                <h3 className="font-semibold text-gray-900 mb-2">
                  {federalRegisterDoc.title}
                </h3>
              ) : (
                <h3 className="font-semibold text-gray-900 mb-2">
                  {a1Document.filename}
                </h3>
              )}
              {federalRegisterDoc?.publication_date ? (
                <p className="text-sm text-gray-600">
                  Published in Federal Register: {new Date(federalRegisterDoc.publication_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </p>
              ) : nprmFiling?.date_received ? (
                <p className="text-sm text-gray-600">
                  Published: {new Date(nprmFiling.date_received).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </p>
              ) : null}
              {federalRegisterDoc?.title && (
                <p className="text-xs text-gray-500 mt-1">
                  Document: {a1Document.filename}
                </p>
              )}
            </div>
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View Full Document</span>
            </a>
          </div>
        </div>
      )}

      {/* Plain-English proposal summary (the high-level brief). Shown on the
          NPRM tab above the questions, including in questionsOnly mode — the
          flag only suppresses the redundant document-info preview header. */}
      {(loadingSummary || nprmSummary || summaryError || commentDeadline) && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/60 p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center space-x-2">
              <Lightbulb className="h-5 w-5 text-emerald-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                What this proposal is about
              </h3>
            </div>
            {commentDeadline && (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-800">
                <CalendarClock className="h-4 w-4" />
                <span>Comments due {formatDeadlineCalendarDate(commentDeadline)}</span>
                {replyCommentDeadline && (
                  <span className="text-amber-600">
                    · replies {formatDeadlineCalendarDate(replyCommentDeadline)}
                  </span>
                )}
              </div>
            )}
          </div>

          {loadingSummary && !nprmSummary ? (
            <div className="flex items-center space-x-2 py-2">
              <Loader className="h-4 w-4 animate-spin text-emerald-500" />
              <span className="text-sm text-gray-600">Summarizing the NPRM…</span>
            </div>
          ) : summaryError && !nprmSummary ? (
            <p className="text-sm text-gray-500">{summaryError}</p>
          ) : nprmSummary ? (
            <div className="space-y-4">
              {nprmSummary.exec_summary && (
                <p className="text-[15px] leading-relaxed text-gray-800">
                  {nprmSummary.exec_summary}
                </p>
              )}

              {nprmSummary.background && (
                <p className="text-sm italic leading-relaxed text-gray-600">
                  {nprmSummary.background}
                </p>
              )}

              {nprmSummary.whats_proposed && nprmSummary.whats_proposed.length > 0 && (
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <FileText className="h-3.5 w-3.5" /> What the FCC is proposing
                  </h4>
                  <ul className="space-y-2">
                    {nprmSummary.whats_proposed.map((item, idx) => (
                      <li
                        key={idx}
                        className="rounded-md border border-emerald-100 bg-white p-3"
                      >
                        {item.title && (
                          <p className="text-sm font-semibold text-gray-900">
                            {item.title}
                          </p>
                        )}
                        {item.detail && (
                          <p className="text-sm leading-relaxed text-gray-700">
                            {item.detail}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                {nprmSummary.who_is_affected && nprmSummary.who_is_affected.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <Users className="h-3.5 w-3.5" /> Who this affects
                    </h4>
                    <ul className="space-y-1">
                      {nprmSummary.who_is_affected.map((who, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                          <span>{who}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {nprmSummary.comment_topics && nprmSummary.comment_topics.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <MessageSquare className="h-3.5 w-3.5" /> What you could weigh in on
                    </h4>
                    <ul className="space-y-1.5">
                      {nprmSummary.comment_topics.map((topic, idx) => {
                        // Backward-compatible: legacy topics are plain strings;
                        // grounded topics are objects with paragraph citations.
                        const label = typeof topic === 'string' ? topic : topic.label;
                        const paras =
                          typeof topic === 'string' ? [] : topic.paragraphs ?? [];
                        return (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                            <span>
                              {label}
                              {paras.length > 0 && (
                                <span className="ml-1.5 text-xs text-gray-400">
                                  ¶ {paras.slice(0, 6).join(', ')}
                                  {paras.length > 6 ? '…' : ''}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* NPRM Questions */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              NPRM Questions {questions.length > 0 && `(${questions.length})`}
            </h3>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {questions.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
                className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-green-700 bg-white hover:bg-green-50 border border-green-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download all questions as a Word document"
              >
                {isDownloadingDocx ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isDownloadingDocx ? 'Preparing…' : 'Download Word'}</span>
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                onClick={handleReprocessQuestions}
                disabled={!nprmFileInStorage || loadingQuestions || uploadingNprm || isReprocessing}
                className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reprocess NPRM questions"
              >
                {isReprocessing ? (
                  <Loader className="w-4 h-4 animate-spin text-gray-500" />
                ) : (
                  <RefreshCcw className="w-4 h-4 text-gray-500" />
                )}
                <span>{isReprocessing ? 'Reprocessing…' : 'Reprocess'}</span>
              </button>
            )}
          </div>
        </div>

        {isAdmin && (reprocessError || reprocessSuccessAt) && (
          <div className="mb-3">
            {reprocessError ? (
              <p className="text-sm text-red-700">{reprocessError}</p>
            ) : (
              <p className="text-sm text-green-700">
                Reprocessed {reprocessSuccessAt ? `at ${new Date(reprocessSuccessAt).toLocaleString()}` : ''}.
              </p>
            )}
          </div>
        )}
        {uploadingNprm ? (
          <div className="flex items-center space-x-2 py-4">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-gray-600">Uploading NPRM file to storage...</span>
          </div>
        ) : !nprmFileInStorage ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            NPRM file needs to be uploaded to storage before questions can be extracted. This should happen automatically...
          </div>
        ) : loadingQuestions ? (
          <div className="flex items-center space-x-2 py-4">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-gray-600">Loading NPRM questions...</span>
          </div>
        ) : questionsError ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
            {questionsError}
          </div>
        ) : questions.length > 0 ? (
          <div>
            {/* Search + type filter */}
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative flex-1 sm:max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search questions, topics, paragraph numbers…"
                  className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
                />
              </div>
              {questionTypes.length > 1 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setSelectedQuestionType('all')}
                    className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                      selectedQuestionType === 'all'
                        ? 'bg-green-600 text-white'
                        : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    All ({questions.length})
                  </button>
                  {questionTypes.map((type) => {
                    const count = questions.filter((q) => q.question_type === type).length;
                    const styles = getNPRMQuestionTypeStyle(type);
                    const isActive = selectedQuestionType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSelectedQuestionType(type)}
                        className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-green-600 text-white'
                            : `${styles.bg} ${styles.text} border ${styles.border} hover:opacity-90`
                        }`}
                      >
                        {NPRM_QUESTION_TYPE_LABEL(type)} ({count})
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {filteredQuestions.length === 0 ? (
              <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                No questions match your search.
              </div>
            ) : (
              <div className="max-h-[min(70vh,720px)] space-y-2.5 overflow-y-auto pr-1">
                {filteredQuestions.map((question) => {
                  const isExpanded = expandedQuestions.has(question.question_id);
                  const typeStyle = getNPRMQuestionTypeStyle(question.question_type);
                  const typeLabel = NPRM_QUESTION_TYPE_LABEL(question.question_type);
                  const topic = question.short_answer?.trim();
                  const para = question.paragraph_number;
                  const page = question.page_number;
                  return (
                    <div
                      key={question.question_id}
                      className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:border-green-300"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const newExpanded = new Set(expandedQuestions);
                          if (newExpanded.has(question.question_id)) {
                            newExpanded.delete(question.question_id);
                          } else {
                            newExpanded.add(question.question_id);
                          }
                          setExpandedQuestions(newExpanded);
                        }}
                        className="w-full px-4 py-3.5 text-left transition-colors hover:bg-gray-50/80"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="mb-1.5 flex flex-wrap items-center gap-2">
                              <span className="inline-flex shrink-0 items-center rounded bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                                Q{question.question_id}
                              </span>
                              {question.question_type && (
                                <span
                                  className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}
                                >
                                  {typeLabel}
                                </span>
                              )}
                              {(para !== undefined && para !== null && para !== '') && (
                                <span className="text-xs text-gray-500">
                                  ¶ {para}
                                  {page ? ` · p. ${page}` : ''}
                                </span>
                              )}
                            </div>
                            {topic && (
                              <p className="mb-1 text-[13px] font-medium text-gray-700">
                                {topic}
                              </p>
                            )}
                            <p
                              className={`text-[15px] leading-relaxed ${
                                isExpanded ? 'text-gray-900' : 'line-clamp-2 text-gray-700'
                              }`}
                            >
                              {question.question_text}
                            </p>
                          </div>
                          <ChevronDown
                            className={`mt-0.5 h-5 w-5 shrink-0 text-gray-400 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            aria-hidden
                          />
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="space-y-3 border-t border-gray-100 bg-gray-50 px-4 pb-4 pt-3">
                          {question.context_summary && (
                            <div>
                              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Why the FCC is asking
                              </h5>
                              <p className="text-sm leading-relaxed text-gray-700">
                                {question.context_summary}
                              </p>
                            </div>
                          )}

                          {question.full_context && (
                            <div>
                              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Context
                              </h5>
                              <p className="text-sm leading-relaxed text-gray-700">
                                {question.full_context}
                              </p>
                            </div>
                          )}

                          {question.seeks_comment_on && question.seeks_comment_on.length > 0 && (
                            <div>
                              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Seeks comment on
                              </h5>
                              <div className="flex flex-wrap gap-1">
                                {question.seeks_comment_on.map((t: string, idx: number) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {question.key_topics && question.key_topics.length > 0 && (
                            <div>
                              <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Key topics
                              </h5>
                              <div className="flex flex-wrap gap-1">
                                {question.key_topics.map((t: string, idx: number) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center rounded bg-green-50 px-2 py-0.5 text-xs text-green-700"
                                  >
                                    {t}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
            {isAdmin ? (
              <>
                <p className="mb-1">No questions found in NPRM.</p>
                <p className="text-xs text-gray-500">
                  The NPRM document may need to be processed first, or the API may not have found questions in this document.
                </p>
              </>
            ) : (
              <p>
                A structured question list isn&rsquo;t available for this NPRM. See{' '}
                <span className="font-medium text-gray-700">What this proposal is about</span> above for
                the proposals and where you can weigh in.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

